import { styled } from '@linaria/react';
import { Link } from 'react-router';
import { Icon } from '../../../../ui-library/icon/icon';
import { Logo } from '../../../../ui-library/logo/logo';
import { AgentLearnedPreferencesView } from '../agent-preferences/agent-learned-preferences-view';
import { AgentMemoriesView } from '../agent-preferences/agent-memories-view';
import { AgentPreferencesForm } from '../agent-preferences/agent-preferences-form';
import { AgentSettingsNav } from './agent-settings-nav';
import { FLORA_AGENT_BASE_PATH } from './agent-settings.constants';
import { FloraSettingsSection } from './agent-settings.type';
import { SettingsConnectionDetail } from './sections/settings-connection-detail';
import { SettingsConnectionsSection } from './sections/settings-connections-section';
import { SettingsGeneralSection } from './sections/settings-general-section';
import { SettingsMemorySection } from './sections/settings-memory-section';
import { SettingsToolsSection } from './sections/settings-tools-section';
import { SettingsSectionIcon } from './settings-section-icon';

interface AgentSettingsLayoutProps {
  section: FloraSettingsSection;
  /** A connection's id when its detail page is open (only with the Connections section). */
  appId: string | null;
}

/**
 * Renders the Flora settings area: a top bar back to the chat, then the grouped
 * menu and the requested section centred together on the page.
 *
 * @param {AgentSettingsLayoutProps} props - The section (and connection) to show
 * @returns {JSX.Element} The settings page
 */
export function AgentSettingsLayout({ section, appId }: AgentSettingsLayoutProps) {
  return (
    <Page>
      <TopBar>
        <BackLink to={FLORA_AGENT_BASE_PATH}>
          <IconSlot aria-hidden="true">
            <Icon name="arrow_back" size={20} color="var(--ink-dark)" pointer />
          </IconSlot>
          Back to chat
        </BackLink>
        <TopBarDivider aria-hidden="true" />
        <Brand>
          <Logo showText={false} width={28} height={28} />
          <BrandName>Flora</BrandName>
          <BrandSuffix>Settings</BrandSuffix>
        </Brand>
        <BrandLine aria-hidden="true" />
      </TopBar>
      <Body>
        <Frame>
          <AgentSettingsNav activeSection={section} />
          <Main>
            <Content>
              {/* Profile has an explicit Save; keeping it mounted (hidden) on the
                other sections means unsaved edits survive switching sections. */}
              <div hidden={section !== FloraSettingsSection.Profile}>
                <AgentPreferencesForm
                  embedded
                  leading={<SettingsSectionIcon section={FloraSettingsSection.Profile} />}
                />
              </div>
              {section !== FloraSettingsSection.Profile && renderSection(section, appId)}
            </Content>
          </Main>
        </Frame>
      </Body>
    </Page>
  );
}

/**
 * Picks the component for a settings section.
 *
 * Profile is rendered by the layout itself (always mounted), so it returns nothing here.
 *
 * @param {FloraSettingsSection} section - The section to show
 * @param {string | null} appId - A connection's id for its detail page
 * @returns {JSX.Element} The section's content
 */
function renderSection(section: FloraSettingsSection, appId: string | null) {
  switch (section) {
    case FloraSettingsSection.Profile:
      return null;
    case FloraSettingsSection.Memory:
      return (
        <SettingsMemorySection>
          <AgentLearnedPreferencesView embedded />
          <AgentMemoriesView embedded />
        </SettingsMemorySection>
      );
    case FloraSettingsSection.General:
      return <SettingsGeneralSection />;
    case FloraSettingsSection.Tools:
      return <SettingsToolsSection />;
    case FloraSettingsSection.Connections:
      return appId ? <SettingsConnectionDetail appId={appId} /> : <SettingsConnectionsSection />;
  }
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  min-height: 0;
  background: var(--sky-lighter);
`;

// Pinned to the top whatever scrolls (the settings body, or the window when
// the page is taller than its frame). Same layer as the sticky AgentViewHeader;
// the design tokens define no z-index scale. Sticky also anchors the absolute
// brand line.
const TopBar = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 10px 24px;
  background: var(--base-white);
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 40px;
  padding: 0 8px;
  border-radius: 8px;
  font-size: var(--font-size-small);
  font-weight: var(--font-weight-medium);
  color: var(--ink-dark);
  text-decoration: none;

  &:hover {
    background: var(--sky-lighter);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 2px;
  }
`;

const TopBarDivider = styled.span`
  width: 1px;
  height: 24px;
  background: var(--sky-light);
`;

const Brand = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandName = styled.span`
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-bold);
  color: var(--ink-darkest);
`;

const BrandSuffix = styled.span`
  font-size: var(--font-size-medium);
  font-weight: var(--font-weight-medium);
  color: var(--ink-light);
`;

// The brand gradient as a thin horizontal line under the Flora header (the
// --gradients-linear token runs top to bottom, so the stops are restated left
// to right from the same palette).
const BrandLine = styled.span`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  background: linear-gradient(
    90deg,
    var(--yellow-base) 0%,
    var(--pink-base) 33%,
    var(--purple-base) 65%,
    var(--secondary-base) 98%
  );
`;

const IconSlot = styled.span`
  display: inline-flex;
`;

// Reserve the scrollbar's space even on sections short enough not to scroll,
// so switching between a short and a long section doesn't shift or resize the
// centred block by the scrollbar's width.
const Body = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-gutter: stable;
`;

// The menu and the section travel together as one centred block, so wide
// screens get even margins on both sides instead of empty space on the right.
const Frame = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Main = styled.main`
  flex: 999 1 560px;
  min-width: 0;
  padding: 32px 48px 56px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 24px 16px 40px;
  }
`;

const Content = styled.div`
  max-width: 880px;
`;

export type { AgentSettingsLayoutProps };
